import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import { initSentry, Sentry } from './services/sentry';
import { iniciarNativo } from './services/nativo';

initSentry();

// Integração com o app nativo (botão Voltar do Android, barra de status).
// No navegador é no-op — o site não muda em nada.
iniciarNativo();

// Fallback do ErrorBoundary GLOBAL — qualquer erro de render não tratado
// derruba o app inteiro pra cá. Causa mais comum em produção: deploy novo
// + Service Worker servindo shell antigo => ChunkLoadError. Por isso o
// botão de recuperação LIMPA OS CACHES do SW antes de recarregar (um
// reload puro muitas vezes volta pro mesmo cache quebrado).
const recuperarApp = async () => {
  try {
    if ('caches' in window) {
      const nomes = await caches.keys();
      await Promise.all(nomes.map((n) => caches.delete(n)));
    }
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.update().catch(() => {})));
    }
  } catch {}
  window.location.reload();
};

// AUTO-RECUPERAÇÃO de ChunkLoadError: quando o app tenta baixar um pedaço de
// código que o deploy novo já substituiu, o usuário via a tela de erro e
// precisava tocar num botão. Agora o app limpa o cache e recarrega SOZINHO,
// uma única vez por sessão (guarda contra loop de reload).
const CHUNK_RE = /Loading chunk|ChunkLoadError|Failed to fetch dynamically imported/i;
const autoRecuperar = (msg) => {
  if (!CHUNK_RE.test(String(msg || ''))) return;
  try {
    if (sessionStorage.getItem('toalugando_chunk_reload') === '1') return;
    sessionStorage.setItem('toalugando_chunk_reload', '1');
  } catch {}
  recuperarApp();
};
window.addEventListener('error', (e) => autoRecuperar(e?.message || e?.error?.message));
window.addEventListener('unhandledrejection', (e) => autoRecuperar(e?.reason?.message || e?.reason));
// Versão nova assumiu o controle (SW trocou) → limpa a trava pra próxima vez
try {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      try { sessionStorage.removeItem('toalugando_chunk_reload'); } catch {}
    });
  }
} catch {}

const FallbackErro = ({ error }) => (
  <div style={{
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#f8fafc', fontFamily: 'system-ui, -apple-system, sans-serif', padding: 24,
  }}>
    <div style={{
      background: '#fff', borderRadius: 20, padding: '32px 28px', maxWidth: 380, width: '100%',
      textAlign: 'center', boxShadow: '0 10px 40px rgba(2,6,23,.08)', border: '1px solid #e2e8f0',
    }}>
      <div style={{
        width: 56, height: 56, borderRadius: '50%', background: '#fff7ed', margin: '0 auto 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26,
      }}>!</div>
      <h2 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
        Ops, algo deu errado
      </h2>
      <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
        Pode ser uma atualização nova do app que não terminou de carregar.
        Toque no botão abaixo que a gente resolve.
      </p>
      <button onClick={recuperarApp} style={{
        width: '100%', padding: '12px 16px', borderRadius: 12, border: 'none', cursor: 'pointer',
        background: '#f97316', color: '#fff', fontSize: 14, fontWeight: 700,
      }}>
        Atualizar e recarregar
      </button>
      <p style={{ margin: '12px 0 0', fontSize: 11, color: '#94a3b8' }}>
        Nossa equipe já foi notificada automaticamente.
      </p>
      {/* Detalhe tecnico do erro - ajuda suporte a diagnosticar pelo print
          do usuario sem precisar abrir o Sentry. */}
      {error?.message && (
        <p style={{
          margin: '14px 0 0', fontSize: 10, color: '#cbd5e1', fontFamily: 'monospace',
          wordBreak: 'break-word', textAlign: 'left', background: '#f8fafc',
          padding: '8px 10px', borderRadius: 8, maxHeight: 80, overflow: 'hidden',
        }}>
          {String(error.message).slice(0, 220)}
        </p>
      )}
    </div>
  </div>
);

// StrictMode removido: causa "Cannot cancel a locked stream" no
// @aws-amplify/ui-react-liveness em dev (duplica mount → SDK panica).
// Em produção StrictMode não tem efeito mesmo.
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <Sentry.ErrorBoundary fallback={({ error }) => <FallbackErro error={error} />}>
    <App />
  </Sentry.ErrorBoundary>
);
